import React, { useState, useEffect } from "react";
import {
  Box,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  Button,
  TextField,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  InputLabel,
  FormControl,
  useMediaQuery,
  } from "@mui/material";
import { Collapse } from "@mui/material";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import HOADocuments from "./HOADocuments";
import SettingsIcon from "@mui/icons-material/Settings";
import NotificationsActiveIcon from "@mui/icons-material/NotificationsActive";
import EmailIcon from "@mui/icons-material/Email";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import MoveToInboxIcon from '@mui/icons-material/MoveToInbox';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import ListAltIcon from "@mui/icons-material/ListAlt";
import SecurityIcon from '@mui/icons-material/Security';
import FlagIcon from '@mui/icons-material/Flag';
import Statuses from "./Statuses";
import Company from "./Company";
import Uploads from "./Uploads";
import Roles from "./Roles";
import WorkOrderManager from "./WorkOrderManager";
import ArchitecturalRequestManager from "./ArchitecturalRequestManager";
import PriorityLevels from "./PriorityLevels";
import Notifications from "./EmailNotifications";
import EmailTemplate from "./EmailTemplate";
import IntegrationSettings from "./IntegrationSettings";
import api, { API_BASE_URL } from "../../utils/api";
import axios from "axios";
import BuildIcon from '@mui/icons-material/Build';
import FolderIcon from "@mui/icons-material/Folder";

const tabList = [
  { label: "General", icon: <SettingsIcon /> },                 // 0
  { label: "Notifications", icon: <NotificationsActiveIcon /> },// 1
  { label: "Email Configuration", icon: <EmailIcon /> },        // 2
  { label: "Uploads", icon: <CloudUploadIcon /> },              // 3
  { label: "Statuses", icon: <ListAltIcon /> },                 // 4
  { label: "Roles", icon: <SecurityIcon /> },                   // 5
  { label: "Priority Levels", icon: <FlagIcon /> },             // 6
  { label: "Email Templates", icon: <MoveToInboxIcon /> },      // 7
  { label: "Integration Settings", icon: <IntegrationInstructionsIcon /> }, // 8
  { label: "HOA Documents", icon: <FolderIcon /> },             // 9 
];

const SystemAdministration = () => {
  const isMobile = useMediaQuery("(max-width:768px)");

  const [typesManagerOpen, setTypesManagerOpen] = useState(true); // default: expanded
  const [tabIndex, setTabIndex] = useState(() => {
    return parseInt(localStorage.getItem("adminTabIndex")) || 0;
  });

  const [loading, setLoading] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(false);
  const [testResult, setTestResult] = useState("");

  const [settings, setSettings] = useState({
    smtpServer: "",
    smtpPort: "587",
    smtpUsername: "",
    smtpPassword: "",
    smtpAuth: true,
    smtpSSL: true,
    fromEmail: "",
    emailFormat: "PDF",
    testRecipient: "",
  });

  useEffect(() => {
    localStorage.setItem("adminTabIndex", tabIndex);
  }, [tabIndex]);

  useEffect(() => {
    api
      .get(`/smtp-settings`)
      .then((res) => {
        if (res.data) {
          setSettings({
            smtpServer: res.data.smtp_server || "",
            smtpPort: res.data.smtp_port || "587",
            smtpUsername: res.data.smtp_username || "",
            smtpPassword: res.data.smtp_password || "",
            smtpAuth: res.data.smtp_auth ?? true,
            smtpSSL: res.data.smtp_ssl ?? false,
            fromEmail: res.data.from_email || "",
            emailFormat: res.data.email_format || "PDF",
            testRecipient: res.data.test_recipient || "",
          });
        }
      })
      .catch(() => setSaveError(true));
  }, []);

  const handleChange = (e) => setSettings({ ...settings, [e.target.name]: e.target.value });
  const handleToggle = (e) => setSettings({ ...settings, [e.target.name]: e.target.checked });

  const handleSave = async () => {
    try {
      await api.post(`/smtp-settings`, settings);
      setSaveSuccess(true);
    } catch {
      setSaveError(true);
    }
  };

  const testSMTPConnection = async () => {
    setLoading(true);
    setTestResult("");
    try {
      const response = await api.post(`/test-smtp`, {
        ...settings,
        toEmail: settings.testRecipient,
      });
      setTestResult(`✅ ${response.data.message}`);
    } catch (error) {
      setTestResult(`❌ SMTP Test Failed: ${error.response?.data?.message || error.message}`);
    }
    setLoading(false);
  };

  const renderTabContent = () => {
    switch (tabIndex) {
      case 0: return <Company />;
      case 1: return <Notifications />;
      case 2: return (
        <Box>
          <Typography variant="h5" gutterBottom>SMTP Settings</Typography>
          <TextField fullWidth label="SMTP Server" name="smtpServer" value={settings.smtpServer} onChange={handleChange} margin="normal" />
          <TextField fullWidth label="SMTP Port" name="smtpPort" value={settings.smtpPort} onChange={handleChange} margin="normal" />
          <TextField fullWidth label="User Name" name="smtpUsername" value={settings.smtpUsername} onChange={handleChange} margin="normal" />
          <TextField fullWidth label="Password" type="password" name="smtpPassword" value={settings.smtpPassword} onChange={handleChange} margin="normal" />
          <FormControlLabel control={<Switch checked={settings.smtpAuth} onChange={handleToggle} name="smtpAuth" />} label="SMTP server requires authentication" />
          <FormControlLabel control={<Switch checked={settings.smtpSSL} onChange={handleToggle} name="smtpSSL" />} label="Enable SSL" />

          <Typography variant="h5" gutterBottom sx={{ mt: 3 }}>Email Settings</Typography>
          <TextField fullWidth label="From Email" name="fromEmail" value={settings.fromEmail} onChange={handleChange} margin="normal" />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Email Format</InputLabel>
            <Select name="emailFormat" value={settings.emailFormat} onChange={handleChange}>
              <MenuItem value="PDF">PDF</MenuItem>
              <MenuItem value="HTML">HTML</MenuItem>
            </Select>
          </FormControl>
          <TextField fullWidth label="Test Recipient Email" name="testRecipient" value={settings.testRecipient} onChange={handleChange} margin="normal" />
          <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3, flexWrap: "wrap", gap: 2 }}>
            <Button variant="contained" color="primary" onClick={handleSave}>Save Settings</Button>
            <Button variant="outlined" color="secondary" onClick={testSMTPConnection} disabled={loading}>
              {loading ? <CircularProgress size={24} /> : "Send Test Email"}
            </Button>
          </Box>
          {testResult && (
            <Typography sx={{ mt: 2, color: testResult.includes("✅") ? "green" : "red" }}>{testResult}</Typography>
          )}
        </Box>
      );
      case 3: return <Uploads />;
      case 4: return <Statuses />;
      case 5: return <Roles />;
      case 6: return <PriorityLevels />;
      case 7: return <EmailTemplate />
      case 8: return <IntegrationSettings />
      case 9: return <HOADocuments />
      case 100: return <WorkOrderManager />
      case 101: return <ArchitecturalRequestManager />
      
     
      default: return null;
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: isMobile ? "column" : "row", minHeight: "100vh", p: 2 }}>
      {/* Sidebar */}
      <Paper elevation={2} sx={{ width: isMobile ? "100%" : 260, mb: isMobile ? 2 : 0 }}>
      <List>
  {tabList.map((tab, index) => (
    <ListItem
      button
      key={tab.label}
      selected={tabIndex === index}
      onClick={() => setTabIndex(index)}
    >
      <ListItemIcon>{tab.icon}</ListItemIcon>
      <ListItemText primary={tab.label} />
    </ListItem>
  ))}

  {/* Types Manager Main Item */}
  <ListItem button onClick={() => setTypesManagerOpen(!typesManagerOpen)}>
    <ListItemIcon><BuildIcon /></ListItemIcon>
    <ListItemText primary="Types Manager" />
    {typesManagerOpen ? <ExpandLess /> : <ExpandMore />}
  </ListItem>

  <Collapse in={typesManagerOpen} timeout="auto" unmountOnExit>
    <List component="div" disablePadding>
      <ListItem
        button
        sx={{ pl: 4 }}
        selected={tabIndex === 100}
        onClick={() => setTabIndex(100)}
      >
        <ListItemIcon><BuildIcon /></ListItemIcon>
        <ListItemText primary="Work Order Manager" />
      </ListItem>

      <ListItem
        button
        sx={{ pl: 4 }}
        selected={tabIndex === 101}
        onClick={() => setTabIndex(101)}
      >
        <ListItemIcon><BuildIcon /></ListItemIcon>
        <ListItemText primary="Architectural Request Manager" />
      </ListItem>
    </List>
  </Collapse>
</List>

      </Paper>

      {/* Content */}
      <Paper sx={{ flex: 1, p: 3 }}>
        {renderTabContent()}
      </Paper>

      {/* Snackbars */}
      <Snackbar open={saveSuccess} autoHideDuration={3000} onClose={() => setSaveSuccess(false)}>
        <Alert severity="success" onClose={() => setSaveSuccess(false)}>Settings saved successfully!</Alert>
      </Snackbar>
      <Snackbar open={saveError} autoHideDuration={3000} onClose={() => setSaveError(false)}>
        <Alert severity="error" onClose={() => setSaveError(false)}>Failed to save settings.</Alert>
      </Snackbar>
    </Box>
  );
};

export default SystemAdministration;
